import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import Link from 'next/link';

import { ICollection, ICollectionWithStoryCounts } from 'types/collection';
import { ISanitizedCompany } from 'types/company';

import { getCollectionPath } from 'services/collectionSlug';

import { FolderIcon } from 'components/creator-toolkit/icons/FolderIcon';
import { GoBackIcon } from 'components/shared/icons/GoBackIcon';
import { GoForwardIcon } from 'components/shared/icons/GoForwardIcon';

interface IProps {
  collections: ICollectionWithStoryCounts[];
  company: ISanitizedCompany;
  inGallery?: boolean;
  galleryAccessKey?: string;
}

export const CollectionsInView = ({
  company,
  collections,
  inGallery,
  galleryAccessKey,
}: IProps) => {
  const [collectionContainerScrollState, setCollectionContainerScrollState] =
    useState<{
      isOverflowing: boolean;
      scrolledLeft: boolean;
      scrolledRight: boolean;
    }>();

  const backgroundColor = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const url = new URL(location.href);
    return url.searchParams.get('bg') || sessionStorage.getItem('bg') || undefined;
  }, []);

  function getCollectionLink(
    company: ISanitizedCompany,
    collection: ICollection,
    inGallery?: boolean,
  ): string {
    const origin = `${window.location.protocol}//${window.location.host}`;
    const collectionPath = getCollectionPath({ collection, company });
    const gallerySuffix = inGallery ? '/gallery' : '';
    const accessKeyQuery = galleryAccessKey
      ? `?accessKey=${galleryAccessKey}`
      : '';

    return `${origin}${collectionPath}${gallerySuffix}${accessKeyQuery}`;
  }

  useEffect(() => {
    const collectionContainer = document.getElementById('collection-container');

    if (collectionContainer) {
      const updateScrollState = () => {
        setCollectionContainerScrollState({
          isOverflowing:
            collectionContainer.scrollWidth > collectionContainer.clientWidth,
          scrolledLeft: collectionContainer.scrollLeft === 0,
          scrolledRight:
            collectionContainer.scrollLeft ===
            collectionContainer.scrollWidth - collectionContainer.clientWidth,
        });
      };

      const frame = requestAnimationFrame(updateScrollState);
      collectionContainer.addEventListener('scroll', updateScrollState);
      window.addEventListener('resize', updateScrollState);

      return () => {
        cancelAnimationFrame(frame);
        collectionContainer.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }
  }, [document.getElementById('collection-container')]);

  const scrollCollections = (direction: 'left' | 'right') => {
    const collectionContainer = document.getElementById('collection-container');

    collectionContainer?.scrollBy({
      left: direction === 'left' ? -235 : 235,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-3 pt-4 sm:px-6">
      {collections && collections.length > 0 && (
        <h3 className="mb-3 text-xl font-medium text-darkGrey dark:text-white/80">
          Collections
        </h3>
      )}

      <div className="relative">
        <div className="hidden md:block">
          {collectionContainerScrollState?.isOverflowing &&
            !collectionContainerScrollState.scrolledLeft && (
              <div
                className={classNames(
                  'absolute left-0 flex h-full w-[28px] items-center dark:bg-mineShaftGrey dark:drop-shadow-[16px_0_6px_rgb(36,36,36)]',
                  {
                    'bg-culturedPearlGrey drop-shadow-[20px_0_6px_rgb(246,246,246)]':
                      !backgroundColor,
                  },
                )}
                style={
                  backgroundColor
                    ? {
                        filter: `drop-shadow(20px 0 6px #${backgroundColor})`,
                        backgroundColor: `#${backgroundColor}`,
                      }
                    : {}
                }
              >
                <button
                  type="button"
                  aria-label="Scroll collections left"
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-carnationRed/20 text-carnationRed"
                  onClick={() => scrollCollections('left')}
                >
                  <GoBackIcon size={15} />
                </button>
              </div>
            )}

          {collectionContainerScrollState?.isOverflowing &&
            !collectionContainerScrollState.scrolledRight && (
              <div
                className={classNames(
                  'absolute right-0 flex h-full w-[28px] items-center dark:bg-mineShaftGrey dark:drop-shadow-[-16px_0_6px_rgb(36,36,36)]',
                  {
                    'bg-culturedPearlGrey drop-shadow-[-20px_0_6px_rgb(246,246,246)]':
                      !backgroundColor,
                  },
                )}
                style={
                  backgroundColor
                    ? {
                        filter: `drop-shadow(-20px 0 6px #${backgroundColor})`,
                        backgroundColor: `#${backgroundColor}`,
                      }
                    : {}
                }
              >
                <button
                  type="button"
                  aria-label="Scroll collections right"
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-carnationRed/20 text-carnationRed"
                  onClick={() => scrollCollections('right')}
                >
                  <GoForwardIcon size={15} />
                </button>
              </div>
            )}
        </div>

        <div
          id="collection-container"
          className="flex gap-[35px] overflow-x-scroll pb-4"
        >
          {collections?.map(collection => {
            const collectionUrl = getCollectionLink(
              company,
              collection.collection,
              inGallery,
            );
            if (collection.publishedStoryCount > 0) {
              return (
                <div key={collection.collection.id}>
                  <Link href={collectionUrl}>
                    <div
                      key={collection.collection.id}
                      className="flex w-[200px] cursor-pointer flex-col rounded-[10px] bg-white p-4 shadow-md dark:bg-white/[8%]"
                    >
                      <div className="mb-[5px] flex justify-between">
                        {collection.collection.emoji ? (
                          <div className="text-[24px] leading-6">
                            {collection.collection.emoji}
                          </div>
                        ) : (
                          <FolderIcon size={24} />
                        )}
                      </div>

                      <p className="mb-[5px] overflow-x-hidden text-ellipsis whitespace-nowrap text-base text-darkGrey dark:text-white/80">
                        {collection.collection.name}
                      </p>

                      <p className="text-[10px] text-grey dark:text-white/70">
                        {collection.publishedStoryCount}{' '}
                        {collection.publishedStoryCount === 1
                          ? 'Story'
                          : 'Stories'}
                      </p>
                    </div>
                  </Link>
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};
